import React from 'react';
import { Collapse, Form } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AdditionalInformationCardContainer } from './AdditionalInformationCard.styles';
import { EPButton } from '@/components';
import { useMeasurement } from '@/hooks/useMeasurement';

export interface AdditionalInformationCardProps {
  fieldNamePath: (string | number)[];
  onEdit?: (hasUrl: boolean) => void;
  defaultActiveKey?: string | string[];
}

const AdditionalInformationCard: React.FC<AdditionalInformationCardProps> = ({
  fieldNamePath,
  onEdit,
  defaultActiveKey = ['1'],
}) => {
  const { t } = useTranslation();
  const { tunit } = useMeasurement();
  const form = Form.useFormInstance();

  const platform = form.getFieldValue([...fieldNamePath, 'platform']);
  const cooling = form.getFieldValue([...fieldNamePath, 'cooling']);
  const pressure = form.getFieldValue([...fieldNamePath, 'pressure']);
  const horsepower = form.getFieldValue([...fieldNamePath, 'horsepower']);
  const specUrl = form.getFieldValue([...fieldNamePath, 'specUrl']);

  const renderMetricRow = (label: string, value: number | string, unit?: string, className?: string) => {
    if (!value) return null;

    return (
      <div className={`metric-row ${className || ''}`}>
        <div className="metric-label">{t(label)}</div>
        <div className="metric-value-container">
          <span className="metric-value">{value}</span>
          {unit && <span className="metric-unit">{unit}</span>}
        </div>
      </div>
    );
  };

  const renderUrlRow = (label: string, url: string, className?: string) => {
    if (!url) return null;

    return (
      <div className={`metric-row ${className || ''}`}>
        <div className="metric-label">{t(label)}</div>
        <div className="metric-value-container">
          <a href={url} target="_blank" rel="noopener noreferrer" className="metric-link">
            {url}
          </a>
        </div>
      </div>
    );
  };

  const items = [
    {
      key: '1',
      label: (
        <div className="collapse-header">
          <span>{t('Spec data')}</span>
          {onEdit && (
            <EPButton type="link" onClick={() => onEdit(!!specUrl)} size="small">
              {t('Edit')}
            </EPButton>
          )}
        </div>
      ),
      children: (
        <div className="spec-content">
          <div className="top-section">
            <div className="basic-info-row">
              {(() => {
                const platformRow = renderMetricRow('Platform', platform || '');
                const coolingRow = renderMetricRow('Cooling type', cooling || '');
                const urlRow = renderUrlRow('URL to compressor model spec', specUrl || '');

                const visibleElements = [platformRow, coolingRow, urlRow].filter(Boolean);

                return visibleElements.map((element, index) => (
                  <React.Fragment key={index}>
                    <div className="metric-wrapper">{element}</div>
                    {index < visibleElements.length - 1 && <div className="divider" />}
                  </React.Fragment>
                ));
              })()}
            </div>
          </div>

          <div className="metrics-grid">
            {renderMetricRow('Pressure', pressure || 0, tunit('bar'))}
            {renderMetricRow('Horse power', horsepower || 0, 'HP')}
          </div>
        </div>
      ),
    },
  ];

  return (
    <AdditionalInformationCardContainer>
      <Collapse
        ghost={true}
        items={items}
        defaultActiveKey={defaultActiveKey}
        expandIcon={({ isActive }) => <CaretDownOutlined rotate={isActive ? 180 : 0} />}
        expandIconPosition="start"
      />
    </AdditionalInformationCardContainer>
  );
};

export default AdditionalInformationCard;
